import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { AlertTriangle, Loader2, ShieldCheck } from 'lucide-react';
import { useNotify, useRecordContext, useUpdate } from 'ra-core';
import { useState } from 'react';
import { useHasPermission } from '../rbac/useHasPermission';
import { useVendorApprovers } from './useVendorApprovers';
import { VENDOR_DOCUMENT_CHECKLIST } from './vendorDocumentChecklist';

export const ApproveVendorButton = () => {
    const record = useRecordContext();
    const notify = useNotify();
    const [update, { isPending }] = useUpdate();
    const [open, setOpen] = useState(false);
    const [approvedBy, setApprovedBy] = useState('');
    const { hasPermission: canManage } = useHasPermission('manage_procurement');
    const approvers = useVendorApprovers();

    if (!record || record.status === 'approved' || record.status === 'conditional' || !canManage) return null;

    const savedDocs = Array.isArray(record.documents) ? record.documents : [];
    const byType = new Map(savedDocs.map((d: any) => [d.doc_type, d]));
    const missingMandatory = VENDOR_DOCUMENT_CHECKLIST.filter(
        (item) => item.mandatory && !(byType.get(item.doc_type) as any)?.file_url
    );
    const hasMissingDocs = missingMandatory.length > 0;

    const handleApprove = () => {
        if (hasMissingDocs) return;
        update(
            'vendors',
            {
                id: record.id,
                data: {
                    status: 'approved',
                    is_approved: true,
                    approved_at: new Date().toISOString(),
                    approved_by: approvedBy || null,
                },
                previousData: record,
            },
            {
                onSuccess: () => {
                    notify('Vendor Approved successfully', { type: 'success' });
                    setOpen(false);
                },
                onError: (error: any) => {
                    notify(`Error approving vendor: ${error.message}`, { type: 'error' });
                },
            }
        );
    };

    return (
        <>
            <Button
                onClick={() => setOpen(true)}
                disabled={isPending}
                variant="default"
                className="bg-emerald-600 hover:bg-emerald-700 text-white gap-2 h-9 px-4 rounded-md shadow-sm transition-all"
            >
                <ShieldCheck className="w-4 h-4" />
                Approve Vendor
            </Button>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Approve Vendor</DialogTitle>
                        <DialogDescription>
                            Select the reviewer approving <span className="font-semibold">{record.name}</span>.
                        </DialogDescription>
                    </DialogHeader>

                    {hasMissingDocs && (
                        <div className="rounded-md border border-red-200 bg-red-50 p-3 text-xs text-red-700 space-y-1">
                            <div className="flex items-center gap-1.5 font-bold">
                                <AlertTriangle className="w-3.5 h-3.5" />
                                Cannot approve — {missingMandatory.length} mandatory document(s) missing
                            </div>
                            <ul className="list-disc pl-5">
                                {missingMandatory.map((d) => (
                                    <li key={d.doc_type}>{d.doc_name}</li>
                                ))}
                            </ul>
                            <p className="text-[11px] text-red-500">Upload them from the vendor's Edit page, then approve.</p>
                        </div>
                    )}

                    <div className="space-y-2 py-2">
                        <Label htmlFor="approved_by">Approved By</Label>
                        <Select value={approvedBy} onValueChange={setApprovedBy}>
                            <SelectTrigger id="approved_by" className="w-full">
                                <SelectValue placeholder="Select reviewer…" />
                            </SelectTrigger>
                            <SelectContent>
                                {approvers.map((a) => (
                                    <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOpen(false)} disabled={isPending}>
                            Cancel
                        </Button>
                        <Button
                            onClick={handleApprove}
                            disabled={isPending || hasMissingDocs}
                            className="bg-emerald-600 hover:bg-emerald-700 text-white gap-2"
                        >
                            {isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
                            Confirm Approval
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
};
